import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import transactionService from '../services/transactionService';
import categoryService from '../services/categoryService';
import creditCardService from '../services/creditCardService';
import CategorySelect from '../components/CategorySelect';

const TransactionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    nature: 'EXPENSE',
    categoryId: '',
    creditCardId: '',
    recurrenceFrequency: '',
    recurrenceCount: 12
  });
  const [errors, setErrors] = useState({});

  const loadTransaction = useCallback(async (transactionId) => {
    try {
      setLoading(true);
      const data = await transactionService.getById(transactionId);
      
      setFormData({
        code: data.code,
        description: data.description,
        amount: Math.abs(data.amount).toString(),
        date: data.date,
        nature: data.nature,
        categoryId: data.categoryId || '',
        creditCardId: data.creditCard?.id || ''
      });
    } catch (error) {
      console.error('Erro ao carregar transação:', error);
      toast.error('Erro ao carregar dados da transação. Tente novamente.');
      navigate('/transactions');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await categoryService.getAll();
        setCategories(data);
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        toast.error('Erro ao carregar categorias. Tente novamente.');
      }
    };

    const loadCreditCards = async () => {
      try {
        const data = await creditCardService.getAll();
        setCreditCards(data);
      } catch (error) {
        console.error('Erro ao carregar cartões de crédito:', error);
        toast.error('Erro ao carregar cartões de crédito. Tente novamente.');
      }
    };
    
    loadCategories();
    loadCreditCards();
    
    if (id) {
      loadTransaction(id);
    }
  }, [id, loadTransaction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.description.trim()) {
      newErrors.description = 'A descrição é obrigatória';
    }
    
    if (!formData.amount) {
      newErrors.amount = 'O valor é obrigatório';
    } else if (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'O valor deve ser um número positivo';
    }
    
    if (!formData.date) {
      newErrors.date = 'A data é obrigatória';
    }
    
    if (!formData.categoryId) {
      newErrors.categoryId = 'A categoria é obrigatória';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const transactionData = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (!transactionData.recurrenceFrequency) {
        delete transactionData.recurrenceFrequency;
        delete transactionData.recurrenceCount;
      } else {
        transactionData.recurrenceCount = parseInt(transactionData.recurrenceCount, 10) || 12;
      }
      
      if (id) {
        await transactionService.update(id, transactionData);
        toast.success('Transação atualizada com sucesso!');
      } else {
        await transactionService.create(transactionData);
        toast.success('Transação criada com sucesso!');
      }
      
      navigate('/transactions');
    } catch (error) {
      console.error('Erro ao salvar transação:', error);
      toast.error('Erro ao salvar transação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <div className="sheet-container">
        <div className="sheet-header">
          <h5 className="sheet-title">{id ? 'Editar Transação' : 'Nova Transação'}</h5>
          <div className="sheet-toolbar">
            <Button 
              variant="outline-secondary"
              onClick={() => navigate('/transactions')}
            >
              <FaArrowLeft className="me-1" /> Voltar
            </Button>
          </div>
        </div>
        
        <div className="sheet-content">
          <Form onSubmit={handleSubmit} className="excel-form" data-testid="transaction-form">
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Código</Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className="excel-form-control"
                    maxLength={10}
                    placeholder="Ex: 01, 07, 34"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.description ? 'is-invalid' : ''}`}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Valor</Form.Label>
                  <Form.Control
                    type="text"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.amount ? 'is-invalid' : ''}`}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Data</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.date ? 'is-invalid' : ''}`}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Natureza</Form.Label>
                  <Form.Select
                    name="nature"
                    value={formData.nature}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.nature ? 'is-invalid' : ''}`}
                  >
                    <option value="EXPENSE">Despesa</option>
                    <option value="REVENUE">Receita</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoria</Form.Label>
                  <CategorySelect
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    categories={categories}
                    className={`excel-form-control ${errors.categoryId ? 'is-invalid' : ''}`}
                    placeholder="Selecione uma categoria"
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-3">
                  <Form.Label>Cartão de Crédito</Form.Label>
                  <Form.Select
                    name="creditCardId"
                    value={formData.creditCardId}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.creditCardId ? 'is-invalid' : ''}`}
                  >
                    <option value="">Selecione um cartão de crédito</option>
                    {creditCards.map(creditCard => (
                      <option key={creditCard.id} value={creditCard.id}>{creditCard.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {!id && (
              <Row>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Recorrência</Form.Label>
                    <Form.Select
                      name="recurrenceFrequency"
                      value={formData.recurrenceFrequency}
                      onChange={handleChange}
                      className="excel-form-control"
                    >
                      <option value="">Nenhuma</option>
                      <option value="WEEKLY">Semanal</option>
                      <option value="MONTHLY">Mensal</option>
                      <option value="YEARLY">Anual</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {formData.recurrenceFrequency && (
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Quantidade</Form.Label>
                      <Form.Control
                        type="number"
                        name="recurrenceCount"
                        value={formData.recurrenceCount}
                        onChange={handleChange}
                        className="excel-form-control"
                        min={2}
                        max={60}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>
            )}
            <div className="sheet-footer">
              <Button 
                variant="primary"
                type="submit"
                disabled={loading}
              >
                <FaSave className="me-1" /> {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default TransactionForm;